import { instance } from '../../../api/api';
import { HttpStatusCode } from 'axios';

export interface FeatureFlag {
  id: string;
  name: string;
  description?: string;
  userIdentifiers: string[];
  createdAt: string;
  updatedAt: string;
  isDeleted: boolean;
}

export interface CreateFeatureFlagData {
  name: string;
  description?: string;
}

export interface UpdateFeatureFlagData {
  name?: string;
  description?: string;
}

export async function getFeatureFlags(auth: string): Promise<FeatureFlag[]> {
  const response = await instance.get('/feature-flags', {
    headers: {
      Authorization: `Bearer ${auth}`,
    },
  });

  if (response.status === HttpStatusCode.Ok && response.data) {
    return response.data;
  }
  throw new Error('Failed to fetch feature flags');
}

export async function createFeatureFlag(
  data: CreateFeatureFlagData,
  auth: string,
): Promise<FeatureFlag> {
  const response = await instance.post('/feature-flags', data, {
    headers: {
      Authorization: `Bearer ${auth}`,
    },
  });

  if (response.status === HttpStatusCode.Created && response.data) {
    return response.data;
  }
  throw new Error('Failed to create feature flag');
}

export async function updateFeatureFlag(
  id: string,
  data: UpdateFeatureFlagData,
  auth: string,
): Promise<FeatureFlag> {
  const response = await instance.put(`/feature-flags/${id}`, data, {
    headers: {
      Authorization: `Bearer ${auth}`,
    },
  });

  if (response.status === HttpStatusCode.Ok && response.data) {
    return response.data;
  }
  throw new Error('Failed to update feature flag');
}

export async function deleteFeatureFlag(
  id: string,
  auth: string,
): Promise<void> {
  const response = await instance.delete(`/feature-flags/${id}`, {
    headers: {
      Authorization: `Bearer ${auth}`,
    },
  });

  if (response.status !== HttpStatusCode.NoContent) {
    throw new Error('Failed to delete feature flag');
  }
}

export async function addUsersToFeatureFlag(
  flagId: string,
  userIds: string[],
  auth: string,
): Promise<FeatureFlag> {
  const response = await instance.post(
    `/feature-flags/${flagId}/users`,
    userIds,
    {
      headers: {
        Authorization: `Bearer ${auth}`,
      },
    },
  );

  if (response.status === HttpStatusCode.Ok && response.data) {
    return response.data;
  }
  throw new Error('Failed to add users to feature flag');
}

export async function removeUsersFromFeatureFlag(
  flagId: string,
  userIds: string[],
  auth: string,
): Promise<FeatureFlag> {
  const response = await instance.delete(`/feature-flags/${flagId}/users`, {
    headers: {
      Authorization: `Bearer ${auth}`,
    },
    data: userIds,
  });

  if (response.status === HttpStatusCode.Ok && response.data) {
    return response.data;
  }
  throw new Error('Failed to remove users from feature flag');
}
